import { useMutation, useQuery } from "@apollo/client";
import BlockOutlined from "@mui/icons-material/BlockOutlined";
import { Button, Stack, Tooltip } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { ConfirmDialog } from "components/ConfirmDialog";
import { StatusChip } from "components/StatusChip";
import { getUtcDisplayDate } from "encamp-shared/src/utils/date";
import { gql } from "generated-graphql";
import { useState } from "react";

import { MANIFEST_BLOCK_REASON_LABELS } from "./manifestBlockReasonDisplay";
import { MarkBlockedDialog } from "./MarkBlockedDialog";

// The deep-link arm of the staff queue query: staff-only, exactly this manifest,
// regardless of tab/windows — the lightest existing way to read the blocked columns.
export const MANIFEST_BLOCK_STATE = gql(`
  query ManifestBlockState($processedManifestId: ID!) {
    staffManifestReviewManifests(processedManifestId: $processedManifestId) {
      items {
        id
        processedManifestId
        blockedReason
        blockedNote
        blockedAt
        blockedBy {
          id
          email
          person {
            first
            last
          }
        }
      }
    }
  }
`);

export const UNBLOCK_PROCESSED_MANIFEST = gql(`
  mutation UnblockProcessedManifest($processedManifestId: ID!) {
    unblockProcessedManifest(processedManifestId: $processedManifestId) {
      processedManifestId
      blockedReason
      blockedNote
      blockedAt
      blockedBy {
        id
      }
    }
  }
`);

type Props = {
  processedManifestId: string;
  manifestTrackingNumber?: string | null;
  /** The Mark blocked dialog's open state lives in the parent so the resolve dialog's "Can't Resolve — Mark as Blocked" can open it too. */
  markBlockedOpen: boolean;
  /** Note carried over from the resolve dialog, when routed from there. */
  markBlockedInitialNote?: string | null;
  onOpenMarkBlocked: () => void;
  onCloseMarkBlocked: () => void;
  /** Called after a successful block — the host dialog closes itself here. */
  onBlocked?: () => void;
};

/**
 * Staff-only header control for the manifest's blocked state: a "Mark blocked" button
 * when workable, or the blocked-reason chip plus an "Unblock" action when blocked.
 * Unblocking never restores assignments — whoever wants the manifest takes it.
 */
export function ManifestBlockControl({
  processedManifestId,
  manifestTrackingNumber,
  markBlockedOpen,
  markBlockedInitialNote,
  onOpenMarkBlocked,
  onCloseMarkBlocked,
  onBlocked,
}: Props) {
  const alerts = useAlerts();
  const [confirmUnblockOpen, setConfirmUnblockOpen] = useState(false);

  const { data } = useQuery(MANIFEST_BLOCK_STATE, {
    variables: { processedManifestId },
  });
  const row = data?.staffManifestReviewManifests.items[0];

  const [unblock, { loading: unblocking }] = useMutation(UNBLOCK_PROCESSED_MANIFEST, {
    refetchQueries: ["ManifestBlockState", "StaffManifestReviewManifests", "ManifestReviewSummary"],
    awaitRefetchQueries: true,
  });

  const handleUnblock = async () => {
    try {
      await unblock({ variables: { processedManifestId } });
      alerts.success("Manifest unblocked — it stays unassigned until someone takes it");
      setConfirmUnblockOpen(false);
    } catch (err) {
      alerts.error("Failed to unblock the manifest", err instanceof Error ? err : undefined);
      setConfirmUnblockOpen(false);
    }
  };

  const blockedBy = row?.blockedBy;
  const blockedByName = blockedBy
    ? `${blockedBy.person?.first ?? ""} ${blockedBy.person?.last ?? ""}`.trim() || blockedBy.email
    : null;
  const tooltip = row?.blockedReason
    ? [
        row.blockedNote,
        row.blockedAt ? `Blocked ${getUtcDisplayDate(row.blockedAt)}` : null,
        blockedByName ? `by ${blockedByName}` : null,
      ]
        .filter(Boolean)
        .join(" · ")
    : "";

  return (
    <Stack direction="row" spacing={1} alignItems="center">
      {row?.blockedReason ? (
        <>
          <Tooltip title={tooltip}>
            <StatusChip
              color="warning"
              variant="outlined"
              label={`Blocked — ${MANIFEST_BLOCK_REASON_LABELS[row.blockedReason]}`}
            />
          </Tooltip>
          <Button size="small" variant="outlined" onClick={() => setConfirmUnblockOpen(true)}>
            Unblock
          </Button>
        </>
      ) : (
        <Button
          size="small"
          variant="outlined"
          startIcon={<BlockOutlined />}
          onClick={onOpenMarkBlocked}
        >
          Mark blocked
        </Button>
      )}

      {confirmUnblockOpen && (
        <ConfirmDialog
          open
          onClose={() => setConfirmUnblockOpen(false)}
          onConfirm={handleUnblock}
          header="Unblock this manifest?"
          info="It returns to the actionable queue unassigned — whoever wants it takes it."
          confirmText="Unblock"
          loading={unblocking}
        />
      )}

      {markBlockedOpen && (
        <MarkBlockedDialog
          open
          processedManifestId={processedManifestId}
          manifestTrackingNumber={manifestTrackingNumber}
          initialNote={markBlockedInitialNote}
          onClose={onCloseMarkBlocked}
          onBlocked={onBlocked}
        />
      )}
    </Stack>
  );
}
